import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone, Share, PlusSquare } from 'lucide-react';
import { Button } from './ui/button';
import { usePWAInstall } from '../lib/pwa';
import { useLanguage } from '../context/LanguageContext';

export default function InstallPWAPrompt() {
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const { language, t } = useLanguage();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user has dismissed before
    const wasDismissed = localStorage.getItem('spenspot_pwa_dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }
    }

    // Show prompt after 30 seconds if can install
    if (canInstall && !isInstalled && !dismissed) {
      const timer = setTimeout(() => setShowPrompt(true), 30000);
      return () => clearTimeout(timer);
    }
  }, [canInstall, isInstalled, dismissed]);

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('spenspot_pwa_dismissed', Date.now().toString());
  };

  const handleInstall = async () => {
    if (isIOS) {
      // Can't auto-install on iOS, just show instructions
      return;
    }
    
    const installed = await promptInstall();
    if (installed) {
      setShowPrompt(false);
    }
  };

  if (!showPrompt || isInstalled || dismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-20 md:bottom-4 left-4 right-4 z-50 max-w-md mx-auto"
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-orange-500 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold">
                    {t('install_spenspot')}
                  </h3>
                  <p className="text-sm text-white/80">
                    {t('quick_access_home_screen')}
                  </p>
                </div>
              </div>
              <button 
                onClick={handleDismiss}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {isIOS ? (
              /* iOS Instructions */
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t('ios_install_instructions')}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Share className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-sm">
                      {t('tap_share')} 
                      <strong>{t('share')}</strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <PlusSquare className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-sm">
                      {t('select_add_home')}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              /* Android/Chrome Install Button */
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t('install_for_offline')}
                </p>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleInstall} 
                    className="flex-1"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('install')}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleDismiss}
                  >
                    {t('later')}
                  </Button>
                </div>
              </div>
            )}

            {/* Benefits */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex justify-around text-center text-xs text-muted-foreground">
                <div>
                  <span className="text-lg">⚡</span>
                  <p>{t('faster')}</p>
                </div>
                <div>
                  <span className="text-lg">📴</span>
                  <p>{t('offline')}</p>
                </div>
                <div>
                  <span className="text-lg">🔔</span>
                  <p>{t('notifications')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Compact install button for settings page
export function InstallButton() {
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const { language, t } = useLanguage();

  if (isInstalled) {
    return (
      <div className="flex items-center gap-2 text-green-600">
        <span className="text-lg">✓</span>
        <span className="text-sm">{t('app_installed')}</span>
      </div>
    );
  }

  if (!canInstall) {
    return null;
  }

  if (isIOS) {
    return (
      <div className="text-sm text-muted-foreground">
        {t('ios_safari_instructions')}
      </div>
    );
  }

  return (
    <Button onClick={promptInstall} variant="outline" size="sm">
      <Download className="w-4 h-4 mr-2" />
      {t('install_app')}
    </Button>
  );
}
